import { useEffect, useRef, useCallback } from 'react';
import { useGameStore } from '../store/game-store.ts';
import { fetchGalaxyMap, fetchFleets, fetchResources, fetchCivilizations, fetchDiplomacy } from '../api/galaxy.ts';
import { syncResources } from '../economy/resource-sync.ts';
import { getAccessToken, refreshSession, notifyLoggedOut } from '../api/session.ts';

// Same origin as the page: Vite proxies /api (incl. WebSocket) in dev, the host serves it in prod
const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/ws`;

/** Server events after which the stockpile or its rates may differ from what the display extrapolates. */
const RESOURCE_EVENTS = new Set([
  'resources.updated', 'construction.started', 'outpost.completed', 'station.completed', 'station.upgraded',
  'ship.build.started', 'ship.build.completed', 'fleet.repair.started', 'fleet.repair.completed', 'fleets.updated',
  'research.completed', 'research.discovery', 'battle.completed', 'system.captured', 'system.battle.resolved',
  'diplomacy.updated', 'galaxy.expanded',
]);

/** M10.2: authenticates with a fresh in-memory access token on every (re)connect. */
export function useWebSocket(enabled: boolean) {
  const wsRef = useRef<WebSocket | null>(null);
  const disposed = useRef(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { updateFleet, updateSystemDiscovery, setFleets, setGalaxyMap, setResources, setKnownCivilizations, updateDiplomacyEntry, addBattle, addConflict, updateConflict, startLiveBattle, addLiveBattleTick, completeLiveBattle } = useGameStore();

  const connect = useCallback(() => {
    if (!enabled || disposed.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = async () => {
      const token = await getAccessToken();
      if (!token) { notifyLoggedOut(); ws.close(); return; }
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'auth', token }));
    };

    ws.onmessage = async (ev) => {
      let msg: Record<string, unknown>;
      try { msg = JSON.parse(ev.data as string) as Record<string, unknown>; }
      catch { return; }

      // Events that spend, refund or pay out resources, or change the production rate: sync the stockpile (shared, de-duplicated request)
      if (typeof msg.type === 'string' && RESOURCE_EVENTS.has(msg.type)) void syncResources();

      if (typeof msg.type === 'string' && ['construction.started', 'outpost.completed', 'station.completed', 'station.upgraded', 'ship.build.started', 'ship.build.completed'].includes(msg.type)) {
        window.dispatchEvent(new Event('galaxis:work-changed'));
      }
      switch (msg.type) {
        case 'connected':
          // On (re)connect, resync everything that may have changed while offline (M10.4: not only map + fleets)
          try {
            const [map, fleetData, res, civs, dip] = await Promise.all([fetchGalaxyMap(), fetchFleets(), fetchResources(), fetchCivilizations(), fetchDiplomacy()]);
            setGalaxyMap(map);
            setFleets(fleetData.fleets);
            setResources(res);
            setKnownCivilizations(civs);
            useGameStore.getState().setDiplomacy(dip);
            useGameStore.getState().bumpDiplomacy();
          } catch { /* ignore sync errors */ }
          break;

        case 'fleet.departed':
        case 'fleet.arrived':
        case 'survey.started':
        case 'survey.completed':
        case 'construction.started':
        case 'outpost.completed':
        case 'station.completed': {
          try {
            const fleetData = await fetchFleets();
            setFleets(fleetData.fleets);
          } catch { /* ignore */ }
          if (msg.type === 'outpost.completed' || msg.type === 'station.completed') {
            try {
              const map = await fetchGalaxyMap();
              setGalaxyMap(map);
            } catch { /* ignore */ }
            try {
              const res = await fetchResources();
              setResources(res);
            } catch { /* ignore */ }
          }
          break;
        }

        case 'station.upgraded':
        case 'resources.updated': {
          try {
            const res = await fetchResources();
            setResources(res);
          } catch { /* ignore */ }
          break;
        }

        case 'galaxy.discovery.updated': {
          const { systemId, discoveryStatus } = msg as {
            systemId: string;
            discoveryStatus: 'unknown' | 'detected' | 'surveyed' | 'colonized';
          };
          // Re-fetch galaxy map to get new system coordinates (for newly detected) or new owner
          try {
            const map = await fetchGalaxyMap();
            setGalaxyMap(map);
            if (discoveryStatus === 'surveyed') {
              const st = useGameStore.getState();
              st.pushToast(`Felmérés kész: ${map.systems.find(x => x.id === systemId)?.name ?? 'rendszer'}`);
              st.flashDiscovery(systemId);
            }
          } catch {
            // Fallback: update in-memory only
            updateSystemDiscovery(systemId, discoveryStatus);
          }
          // On colonization, civ list may have changed (new civ became visible)
          if (discoveryStatus === 'colonized') {
            try {
              const civs = await fetchCivilizations();
              setKnownCivilizations(civs);
            } catch { /* ignore */ }
          }
          break;
        }

        case 'discovery.revealed': {
          // Stellaris fog: a ship of ours just revealed a system's name / a foreign civilization
          const m = msg as { kind: 'system' | 'civilization'; systemId: string; systemName: string; civilizationName?: string };
          const st = useGameStore.getState();
          st.pushToast(m.kind === 'system' ? `Új rendszer felfedezve: ${m.systemName}` : `Idegen civilizáció: ${m.civilizationName ?? '?'} (${m.systemName})`);
          st.flashDiscovery(m.systemId);
          try { setGalaxyMap(await fetchGalaxyMap()); } catch { /* ignore */ }
          if (m.kind === 'civilization') { try { setKnownCivilizations(await fetchCivilizations()); } catch { /* ignore */ } }
          break;
        }

        case 'app.version': {
          // M10.8: a deployment finished; the banner decides whether this client has to update
          const { version, minClientVersion } = msg as { version: string; minClientVersion: string | null };
          window.dispatchEvent(new CustomEvent('galaxis:app-version', { detail: { version, minClientVersion } }));
          break;
        }

        case 'galaxy.expanded': {
          // M10.5: the galaxy grew — refetch this player's (fog-of-war filtered) map
          try { setGalaxyMap(await fetchGalaxyMap()); } catch { /* ignore */ }
          break;
        }

        case 'diplomacy.updated': {
          const { withCivId, status } = msg as { withCivId: string; status?: 'neutral' | 'hostile' };
          if (status) updateDiplomacyEntry(withCivId, status);
          useGameStore.getState().bumpDiplomacy();
          try {
            // M10.4: a treaty can change the map (alliance sharing) and the contact list too
            const [entries, res, map, civs] = await Promise.all([fetchDiplomacy(), fetchResources(), fetchGalaxyMap(), fetchCivilizations()]);
            useGameStore.getState().setDiplomacy(entries);
            setResources(res);
            setGalaxyMap(map);
            setKnownCivilizations(civs);
          } catch { /* ignore */ }
          break;
        }

        case 'battle.completed': {
          const { battle } = msg as { battle: import('@galaxis/shared').BattleReport };
          addBattle(battle);
          break;
        }

        case 'system.attack.started':
        case 'system.occupation.started': {
          const { conflictId, systemId } = msg as { conflictId: string; systemId: string };
          // Fetch the conflict record to display occupation progress
          try {
            const { fetchSystemConflict } = await import('../api/galaxy.ts');
            const conflict = await fetchSystemConflict(systemId);
            if (conflict && conflict.id === conflictId) addConflict(conflict);
          } catch { /* ignore */ }
          break;
        }

        case 'system.battle.resolved':
        case 'system.occupation.interrupted': {
          const { conflictId, systemId } = msg as { conflictId: string; systemId: string };
          try {
            const { fetchSystemConflict } = await import('../api/galaxy.ts');
            const conflict = await fetchSystemConflict(systemId);
            if (conflict && conflict.id === conflictId) updateConflict(conflict);
          } catch { /* ignore */ }
          break;
        }

        case 'system.captured': {
          const { systemId } = msg as { systemId: string };
          try {
            const [map, { fetchSystemConflict }] = await Promise.all([
              fetchGalaxyMap(),
              import('../api/galaxy.ts'),
            ]);
            setGalaxyMap(map);
            // M10.4: ownership moves production and the fleets that were in the system
            const [fleetData, res] = await Promise.all([fetchFleets(), fetchResources()]);
            setFleets(fleetData.fleets);
            setResources(res);
            const conflict = await fetchSystemConflict(systemId);
            if (conflict) updateConflict(conflict);
          } catch { /* ignore */ }
          break;
        }

        case 'battle.live.started': {
          const { battleId, replayData, attackerCivId, defenderCivId } = msg as {
            battleId: string;
            replayData: import('@galaxis/shared').BattleReplayData;
            attackerCivId: string;
            defenderCivId: string;
          };
          startLiveBattle(battleId, replayData, attackerCivId, defenderCivId);
          break;
        }

        case 'battle.tick': {
          const { battleId, events } = msg as {
            battleId: string;
            tick: number;
            events: import('@galaxis/shared').BattleEvent[];
          };
          addLiveBattleTick(battleId, events);
          break;
        }

        case 'battle.live.completed': {
          const { battleId, winner } = msg as {
            battleId: string;
            winner: 'attacker' | 'defender' | 'draw';
          };
          completeLiveBattle(battleId, winner);
          break;
        }

        case 'fleet.command.failed':
        case 'fleet.command.updated': {
          const { message, type } = msg as { message: string | null; type: string };
          if (type === 'fleet.command.failed' && message) useGameStore.getState().pushToast(message, 'error');
          try {
            const fleetData = await fetchFleets();
            setFleets(fleetData.fleets);
          } catch { /* ignore */ }
          break;
        }

        case 'fleets.updated': {
          try {
            const fleetData = await fetchFleets();
            setFleets(fleetData.fleets);
          } catch { /* ignore */ }
          break;
        }

        case 'fleet.repair.started':
        case 'fleet.repair.completed':
        case 'ship.build.started':
        case 'ship.build.completed': {
          // Refresh fleets (new ship joined a fleet) and resources (costs deducted)
          try {
            const [fleetData, res] = await Promise.all([fetchFleets(), fetchResources()]);
            setFleets(fleetData.fleets);
            setResources(res);
          } catch { /* ignore */ }
          break;
        }

        case 'research.discovery': {
          const { kind } = msg as { kind: 'archive' | 'wreck' | 'artifact' };
          const { DISCOVERY_INFO } = await import('@galaxis/shared');
          useGameStore.getState().pushToast(`Felfedezés: ${DISCOVERY_INFO[kind]?.name ?? 'lelet'}`);
          useGameStore.getState().bumpResearch();
          break;
        }

        case 'research.completed': {
          const { techId } = msg as { techId: string };
          const { TECH_BY_ID } = await import('@galaxis/shared');
          useGameStore.getState().pushToast(`Kutatás kész: ${TECH_BY_ID.get(techId)?.name ?? techId}`);
          useGameStore.getState().bumpResearch();
          break;
        }

        case 'ping':
          ws.send(JSON.stringify({ type: 'pong', timestamp: new Date().toISOString() }));
          break;
      }
    };

    ws.onclose = async (ev) => {
      wsRef.current = null;
      if (disposed.current) return;
      // Session revoked (logout elsewhere, password change): stop and go to the login screen
      if (ev.code === 4001) { notifyLoggedOut(); return; }
      // Rejected token: get a new one first; no session left means signed out
      if (ev.code === 1008 && ev.reason === 'Invalid token' && !(await refreshSession())) { notifyLoggedOut(); return; }
      // Auto-reconnect after 3 seconds
      reconnectTimer.current = setTimeout(() => connect(), 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [enabled, updateFleet, updateSystemDiscovery, setFleets, setGalaxyMap, setResources, setKnownCivilizations, updateDiplomacyEntry, addBattle, addConflict, updateConflict, startLiveBattle, addLiveBattleTick, completeLiveBattle]);

  useEffect(() => {
    disposed.current = false;
    connect();
    return () => {
      disposed.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);
}
